import * as React from "react";
import { cn } from "../../lib/utils";

interface TooltipProps {
  children: React.ReactNode;
  content: string;
  className?: string;
  side?: "top" | "bottom" | "left" | "right";
  immediate?: boolean; // Show immediately (for icons) vs with delay (for text)
  delayLong?: boolean; // Show with 2-second delay (for main canvas elements)
  autoPosition?: boolean; // Automatically reposition to stay in viewport
}

const Tooltip: React.FC<TooltipProps> = ({ children, content, className, side = "top", immediate = false, delayLong = false, autoPosition = true }) => {
  const [isVisible, setIsVisible] = React.useState(false);
  const [isHovered, setIsHovered] = React.useState(false);
  const [actualSide, setActualSide] = React.useState(side);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const tooltipRef = React.useRef<HTMLDivElement | null>(null);

  const showDelay = immediate ? 0 : delayLong ? 2000 : 700;

  // Check if tooltip would be clipped and reposition if needed
  const checkAndRepositionTooltip = () => {
    if (!autoPosition || !containerRef.current) {
      setActualSide(side);
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    
    // Use actual tooltip dimensions if available, otherwise use estimates
    const tooltipHeight = tooltipRef.current?.offsetHeight || 120; // Added buffer
    const tooltipWidth = tooltipRef.current?.offsetWidth || 256; // w-64 = 256px
    
    // Add buffer space to prevent edge cutoff
    const buffer = 20;
    const arrowSize = 8; // 4px border * 2

    let newSide = side;

    // Check vertical positioning with buffer
    if (side === "top" && rect.top < tooltipHeight + buffer + arrowSize) {
      newSide = "bottom";
    } else if (side === "bottom" && rect.bottom + tooltipHeight + buffer + arrowSize > viewportHeight) {
      newSide = "top";
    }

    // Check horizontal positioning with buffer
    if (side === "left" && rect.left < tooltipWidth + buffer + arrowSize) {
      newSide = "right";
    } else if (side === "right" && rect.right + tooltipWidth + buffer + arrowSize > viewportWidth) {
      newSide = "left";
    }

    // Additional check for vertical sides to ensure they don't go off screen vertically
    if ((newSide === "left" || newSide === "right")) {
      const centerY = rect.top + rect.height / 2;
      if (centerY - tooltipHeight / 2 < buffer) {
        newSide = "bottom";
      } else if (centerY + tooltipHeight / 2 > viewportHeight - buffer) {
        newSide = "top";
      }
    }

    // Additional check for horizontal sides to ensure they don't go off screen horizontally
    if ((newSide === "top" || newSide === "bottom")) {
      const centerX = rect.left + rect.width / 2;
      if (centerX - tooltipWidth / 2 < buffer) {
        newSide = "right";
      } else if (centerX + tooltipWidth / 2 > viewportWidth - buffer) {
        newSide = "left";
      }
    }

    setActualSide(newSide);
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    if (showDelay === 0) {
      checkAndRepositionTooltip();
      setIsVisible(true);
    } else {
      timeoutRef.current = setTimeout(() => {
        checkAndRepositionTooltip();
        setIsVisible(true);
      }, showDelay);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsVisible(false);
  };

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Recalculate position after tooltip is rendered
  React.useEffect(() => {
    let animationFrameId: number | null = null;
    
    if (isVisible && tooltipRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      animationFrameId = requestAnimationFrame(() => {
        checkAndRepositionTooltip();
      });
    }
    
    // Cleanup function to cancel the animation frame
    return () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isVisible, content]); // Re-run when visibility or content changes

  const getTooltipClasses = () => {
    const visibilityClasses = isVisible ? "visible opacity-100" : "invisible opacity-0";
    const baseClasses = `${visibilityClasses} absolute z-[9999] px-3 py-2 text-xs bg-popover text-popover-foreground border rounded shadow-lg w-64 leading-relaxed transition-opacity duration-200 break-words`;
    
    switch (actualSide) {
      case "top":
        return cn(baseClasses, "-top-3 left-1/2 transform -translate-x-1/2 -translate-y-full");
      case "bottom":
        return cn(baseClasses, "top-full mt-3 left-1/2 transform -translate-x-1/2");
      case "left":
        return cn(baseClasses, "right-full mr-3 top-1/2 transform -translate-y-1/2");
      case "right":
        return cn(baseClasses, "left-full ml-3 top-1/2 transform -translate-y-1/2");
      default:
        return cn(baseClasses, "-top-3 left-1/2 transform -translate-x-1/2 -translate-y-full");
    }
  };

  const getArrowClasses = () => {
    const baseClasses = "absolute w-0 h-0";
    
    switch (actualSide) {
      case "top":
        // Arrow at bottom pointing down
        return cn(baseClasses, "top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-popover");
      case "bottom":
        // Arrow at top pointing up
        return cn(baseClasses, "bottom-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-b-4 border-transparent border-b-popover");
      case "left":
        // Arrow on right pointing right
        return cn(baseClasses, "left-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-l-4 border-transparent border-l-popover");
      case "right":
        // Arrow on left pointing left
        return cn(baseClasses, "right-full top-1/2 transform -translate-y-1/2 border-t-4 border-b-4 border-r-4 border-transparent border-r-popover");
      default:
        return cn(baseClasses, "top-full left-1/2 transform -translate-x-1/2 border-l-4 border-r-4 border-t-4 border-transparent border-t-popover");
    }
  };

  return (
    <div 
      ref={containerRef}
      className={cn("relative", className)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {children}
      <div ref={tooltipRef} className={getTooltipClasses()}>
        {content}
        <div className={getArrowClasses()}></div>
      </div>
    </div>
  );
};

export { Tooltip }; 